<script>
  import Section from './Section.svelte';
  import Modal from './Modal.svelte';
  import config from '../config.json';
  import CaptureStore from '../stores/capture.store';
  import CaptureService from '../services/capture.service';
  import Logger from '../helpers/logger';

  const noDevices = { name: 'No devices' };
  let error = false;
  let modal = { title: '', message: '' };
  let captureHandle = $CaptureStore.captureHandle;
  let devices = CaptureService.getAllDevices();
  let deviceSelected = devices.length > 0 ? devices[0] : noDevices;
  let openCloseButton = captureHandle ? 'Close' : 'Open';
  let openCloseDeviceButton = deviceSelected.handle ? 'Close' : 'Open';
  let deviceNotifications = deviceSelected.handle ? CaptureService.getDeviceNotifications(deviceSelected.handle) : [];

  let credentialsName = $CaptureStore.credentialsName || Object.keys(config.credentials)[0];
  let appId = config.credentials[credentialsName].appId;
  let developerId = config.credentials[credentialsName].developerId;
  let appKey = config.credentials[credentialsName].appKey;
  CaptureService.setNotification((handle, devicesListChanged) => {
    Logger.log(`Receive something from Capture handle: ${handle} devices list changed: ${devicesListChanged}`);
    if(devicesListChanged){
      devices = CaptureService.getAllDevices();
    }
    else{
      deviceNotifications = CaptureService.getDeviceNotifications(handle) || [];
      Logger.log('device notifications: ', deviceNotifications);
    }
  });

  const onOpenCloseCapture = async () => {
    if(openCloseButton === 'Open'){
      Logger.log('About to open Capture with ', {appId, developerId, appKey});
      try{
        const client = await CaptureService.openClient(config.companionUrl, {appId, developerId, appKey});
        captureHandle = client.handle;
        $CaptureStore.captureHandle = captureHandle;
        openCloseButton = 'Close';
        setTimeout(() => {
          deviceSelected = noDevices;
          devices = CaptureService.getAllDevices();
          if(devices.length){
            deviceSelected = devices[0];
          }
          Logger.log('devices after open: ', devices);
        }, 500);
      }
      catch(err) {
        error = true;
        modal.title = 'Error during Open Capture';
        modal.message = err.message;
      }
    }
    else{
      Logger.log('About to close Capture');
      try {
        await CaptureService.closeClient();        
      } catch (err) {
        error = true;
        modal.title = 'Error during Close Capture';
        modal.message = err.message;
      }
      captureHandle = undefined;
      $CaptureStore.captureHandle = undefined;
      openCloseButton = 'Open';
      devices = [];
      deviceSelected = noDevices;
    }
  }

  const onOpenCloseDevice = async () => {
    if(openCloseDeviceButton === 'Open'){
      const { guid } = deviceSelected;
      Logger.log('About to open device with ', { handle: captureHandle, deviceGuid: guid });
      try{
        const deviceHandle = await CaptureService.openDevice(captureHandle, guid);
        deviceSelected.handle = deviceHandle;
        openCloseDeviceButton = 'Close';
        setTimeout(() => {
          deviceNotifications = CaptureService.getDeviceNotifications(deviceHandle);
        }, 500);
      }
      catch(err) {
        error = true;
        modal.title = 'Error during Open Device';
        modal.message = err.message;
      }
    }
    else{
      Logger.log('About to close device');
      const handle = deviceSelected.handle;
      try {
        await CaptureService.closeDevice(handle);        
      } catch (err) {
        error = true;
        modal.title = 'Error during Close Device';
        modal.message = err.message;
      }
      deviceSelected.handle = undefined;
      openCloseDeviceButton = 'Open';
      deviceNotifications = [];
    }
  }

  const onDeviceSelectedChange = () => {
    Logger.log('device selected changed: ', deviceSelected);
    try {
      if(deviceSelected.handle){
        deviceNotifications = CaptureService.getDeviceNotifications(deviceSelected.handle);
      }
      else {
        deviceNotifications = [];
      }
    } catch (err) {
      error = true;
      modal.title = 'Error during Device Selection';
      modal.message = err.message;
    }
    openCloseDeviceButton = deviceSelected.handle ? 'Close' : 'Open';
  };
</script>

<h2>Capture</h2>
<Section name="Client">
  <form on:submit|preventDefault={onOpenCloseCapture}>
    <div class="column">
      <label>App ID
        <input bind:value={appId} disabled={captureHandle}>
      </label>
      <label>Developer ID
        <input bind:value={developerId} disabled={captureHandle}>
      </label>
      <label>App Key
        <input bind:value={appKey} disabled={captureHandle}>
      </label>
    </div>
    <div class="column">
      <div class="bottom-right">
        <button type="submit" value="Open">{openCloseButton}</button>
      </div>
    </div>
  </form>
</Section>
<Section name="Device">
  <form on:submit|preventDefault={onOpenCloseDevice}>
    <div class="column">
      <label>Devices present
        <select bind:value={deviceSelected} on:change={onDeviceSelectedChange}>
          {#each devices as device}
          <option value={device}>{device.name}</option>
          {:else}
          <option value={noDevices} selected="true">{noDevices.name}</option>
          {/each}
        </select>
      </label>
    </div>
    <div class="column">
      <div class="bottom-right">
        <button type="submit" disabled={devices.length === 0}>{openCloseDeviceButton}</button>
      </div>
    </div>
  </form>
  <label>Notifications
    <textarea cols="30" rows="10" value={deviceNotifications.join('\n')}></textarea>
  </label>
</Section>
{#if error}
<Modal>
  <div class="modal-container">
    <div class="modal-title">
      <h2>{modal.title}</h2>
    </div>
    <div class="center">
      <h3>{@html modal.message}</h3>
      <button on:click={()=>{error = false;}}>OK</button>
    </div>
  </div>
</Modal>
{/if}
<style>
  h2 {
    margin: 10px;
  }

  label {
    display: flex;
    gap: 5px;
    margin: 5px;
    flex-direction: column;
    font-size: 1.2em;
  }
  
  input {
    padding: 5px;
    border: none;
    border-radius: 5px;
    font-size: 1.2em;
  }
  
  button {
    padding: 10px 30px;
    font-size: 1.2em;
    border-radius: 5px;
  }

  select {
    font-size: 1.2em;
    padding: 5px;
    border-radius: 5px;
  }

  form {
    display: flex;
    gap: 10px;
  }

  textarea {
    width: 100%;
    font-size: 1.2em;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif;
    border-radius: 5px;
    resize: vertical;
  }

  .column {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 5px;
  }

  .bottom-right {
    flex: 1;
    display: flex;
    flex-direction: column-reverse;
  }

  .bottom-right button {
    align-self: flex-end;
  }

  .modal-container {
    text-align: center;
    background-color: #D77C33;
  }

  .center {
    padding: 10px;
    background-color: #DDDDBE;
    border-radius: 5px;
  }

  .center button {
    margin-top: 20px;
  }
	@media  screen and (max-width: 812px) {
		form {
			flex-direction: column;
		}
    .bottom-right {
      position:relative;
    }
    .bottom-right button{
      position:relative;
    }
	}
</style>
